import type { WidgetDefinition } from '../WidgetDefinition';
import { faTableColumns } from '@fortawesome/pro-regular-svg-icons/faTableColumns';
import type { Idea, WatchlistPlan } from '@thinkalpha/platform-ws-client/contracts/ideas/index.js';
import { generatorRoutine } from '@thinkalpha/ui-core/routine/index';
import type { WatchlistWidgetModel } from '@thinkalpha/ui-types/contracts/workspace';
import { createDeferredIdeaHydration, shouldDeferIdeaHydration } from 'src/lib/widget/deferredIdeaHydration';
import type { WatchlistWidgetViewModel } from 'src/store/types';

function createPlaceholderWatchlistIdea(): Idea & { plan: WatchlistPlan } {
    return {
        name: 'Unnamed Watchlist',
        key: 'ticker',
        description: 'Watchlist',
        plan: { type: 'watchlist', includeList: [] },
        defaultUniverseId: null,
        defaultColumnTemplateId: null,
        isTemplate: false,
    };
}

class WatchlistWidgetDefinition implements WidgetDefinition<WatchlistWidgetViewModel, WatchlistWidgetModel> {
    type = 'watchlist' as const;
    nameKey = 'widgets::watchlist::name' as const;
    roles = ['widgets::watchlist'];
    icon = faTableColumns;
    category = 'widgets::categories::research' as const;

    /**
     * Converts a Watchlist widget from contract model to view model
     */
    toViewModel = generatorRoutine(function* (_ctx, widget: WatchlistWidgetModel) {
        const { idea: incomingIdea, columnOptions, ...restWidget } = widget;

        let idea: WatchlistWidgetViewModel['idea'];
        let isIdeaDirty = false;
        const ideaHydration = shouldDeferIdeaHydration(incomingIdea)
            ? createDeferredIdeaHydration(incomingIdea)
            : undefined;

        if (!ideaHydration && typeof incomingIdea !== 'string') {
            idea = incomingIdea;
            isIdeaDirty = idea.plan.includeList.length > 0; // Only dirty if there are symbols in the plan
        } else {
            idea = createPlaceholderWatchlistIdea();
        }

        return {
            ...restWidget,
            idea,
            columnOptions,
            isIdeaDirty,
            ...(ideaHydration ? { ideaHydration } : {}),
        };
    });

    /**
     * Converts a Watchlist widget from view model to contract model
     */
    toContractModel(widget: WatchlistWidgetViewModel): WatchlistWidgetModel {
        const { idea, ideaHydration, isIdeaDirty, defaultIdeaStatus: _defaultIdeaStatus, ...restWidget } = widget;

        return {
            ...restWidget,
            idea:
                ideaHydration && ideaHydration.status !== 'ready'
                    ? ideaHydration.source
                    : idea.id && isIdeaDirty === false
                      ? idea.id
                      : idea,
            type: 'watchlist',
        };
    }
}

export const watchlistWidgetDefinition = new WatchlistWidgetDefinition();
